import React, { useRef, useState, useEffect } from "react";
import { Box, VStack } from "@chakra-ui/react";
import { MainSidebar, BaseMap } from "../";
import Toolbar from "../Toolbar";
import Team from "../Team";
import "intro.js/introjs.css";

import "./Landing.scss";
import { setProject } from "../../features/viewMode/viewModeSlice";
import { useDispatch } from "react-redux";
import { SectionTitle } from "./SectionTitle";
import { SectionDescriptions } from "./SectionDescriptions";

type ActiveView = "landing" | "map" | "team";

const Landing: React.FC = () => {
    const dispatch = useDispatch();
    const hash = window.location.hash;
    const isProject = hash === "#culiacan_sur" || hash === "#culiacan_centro";
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const [calculatedOpacity, setCalculatedOpacity] = useState(1);
    const [activeView, setActiveView] = useState<ActiveView>(
        isProject ? "map" : "landing"
    );

    const handleScroll = () => {
        const scrollContainer = scrollContainerRef.current;
        if (scrollContainer) {
            const scrollPosition = scrollContainer.scrollTop;
            const scrollHeight = scrollContainer.clientHeight;
            const scrollPercentageCalc = scrollPosition / scrollHeight;

            if (scrollPercentageCalc > 0 && scrollPercentageCalc > 1) {
                const opacity = 2 - scrollPercentageCalc;
                setCalculatedOpacity(opacity > 0.95 ? 1 : opacity);
            } else {
                setCalculatedOpacity(1);
            }
        }
    };

    const handleActivateLanding = () => {
        setCalculatedOpacity(0.1);
        setActiveView("landing");
        if (scrollContainerRef.current) {
            scrollContainerRef.current.scrollTo({
                top:
                    scrollContainerRef.current.scrollTop -
                    scrollContainerRef.current.clientHeight,
                behavior: "smooth",
            });
        }
    };

    useEffect(() => {
        if (calculatedOpacity <= 0) {
            dispatch(setProject("culiacan_sur"));
            setActiveView("map");
        }
    }, [calculatedOpacity, dispatch]);

    useEffect(() => {
        const scrollContainer = scrollContainerRef.current;
        if (scrollContainer) {
            scrollContainer.addEventListener("scroll", handleScroll);
        }
    }, []);

    useEffect(() => {
        // Function to scroll to the element based on the hash
        const scrollToHashElement = () => {
            const hash = window.location.hash;
            if (hash) {
                const element = document.querySelector(hash);
                if (element) {
                    element.scrollIntoView({ behavior: "instant" });
                }
            }
        };

        scrollToHashElement();

        window.addEventListener("hashchange", scrollToHashElement);

        return () => {
            window.removeEventListener("hashchange", scrollToHashElement);
        };
    }, []);

    return (
        <>
            <Box ref={scrollContainerRef} h="100vh" overflowY="scroll">
                <VStack gap={0}>
                    {activeView === "landing" && (
                        <>
                            <SectionTitle
                                onGoToTeam={() => setActiveView("team")}
                            />
                            <SectionDescriptions
                                calcOpacity={calculatedOpacity}
                            />
                        </>
                    )}

                    {activeView === "map" && (
                        <>
                            <BaseMap />
                            <Box
                                position={"absolute"}
                                left={0}
                                height={"100vh"}
                                width={"auto"}
                                display={"flex"}
                                justifyContent={"space-between"}
                            >
                                <MainSidebar />
                                <Toolbar
                                    handleActivateLanding={
                                        handleActivateLanding
                                    }
                                />
                            </Box>
                        </>
                    )}

                    {activeView === "team" && (
                        <Team onBack={() => setActiveView("landing")} />
                    )}
                </VStack>
            </Box>
        </>
    );
};

export default Landing;
